import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Bot, Clock, MessageSquare, Lightbulb, ShoppingCart, DollarSign, 
  TrendingUp, Search, MoreHorizontal, Settings, BarChart3
} from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';
import { useToast } from '../../hooks/use-toast';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAIAdvisors = () => {
  const { branding } = useBranding();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [timeFilter, setTimeFilter] = useState('all');
  
  // Stats data (would come from backend in production)
  const [stats, setStats] = useState({
    timeSaved: '4 hours',
    conversations: 8,
    recommendations: 79,
    ordersSold: 2,
    totalSales: 1256.91,
    avgOrderValue: 628.46
  });

  // Sales data
  const [sales, setSales] = useState([
    { id: 1, name: 'Dali Ogden', recommendation: 'Signature HydraFacial', source: 'Advisor Conversation', value: 283.16, date: 'Jan 22, 2026 02:45 PM' },
    { id: 2, name: 'Odessa Hambric', recommendation: 'Radiesse', source: 'Advisor Conversation', value: 973.75, date: 'Nov 07, 2025 03:28 PM' }
  ]);

  // Recommendations data
  const [recommendations, setRecommendations] = useState([
    { id: 1, item: 'Combo Filler', count: 12, lastRecommended: 'Feb 27, 2026 03:59 PM' },
    { id: 2, item: 'Combo Filler & PDO Thread', count: 10, lastRecommended: 'Feb 27, 2026 03:59 PM' },
    { id: 3, item: 'Dermal Fillers', count: 9, lastRecommended: 'Feb 27, 2026 03:59 PM' },
    { id: 4, item: 'Facials', count: 9, lastRecommended: 'Feb 27, 2026 03:59 PM' },
    { id: 5, item: 'Chemical Peels', count: 8, lastRecommended: 'Jan 26, 2026 12:53 PM' },
    { id: 6, item: 'Microneedling', count: 6, lastRecommended: 'Feb 21, 2026 01:19 PM' },
    { id: 7, item: 'Laser Genesis', count: 4, lastRecommended: 'Jan 20, 2026 10:12 AM' },
    { id: 8, item: 'Waxing', count: 3, lastRecommended: 'Jan 31, 2026 03:24 PM' },
    { id: 9, item: 'Hydrafacial', count: 3, lastRecommended: 'Jan 20, 2026 10:12 AM' },
    { id: 10, item: 'Kybella', count: 3, lastRecommended: 'Jan 31, 2026 03:24 PM' }
  ]);

  // Conversations data
  const [conversations, setConversations] = useState([
    { id: 1, name: 'S Blake', recommendations: ['Laser Genesis', 'Limelight IPL'], date: 'Feb 25, 2026 09:14 PM' },
    { id: 2, name: 'Dali Ogden', recommendations: ['Signature HydraFacial'], date: 'Jan 22, 2026 02:11 PM' },
    { id: 3, name: 'Donna Evans', recommendations: [], date: 'Jan 09, 2026 04:55 PM' },
    { id: 4, name: 'Rachel Robertson', recommendations: [], date: 'Jan 08, 2026 01:00 PM' }
  ]);

  const [searchSales, setSearchSales] = useState('');
  const [searchRecommendations, setSearchRecommendations] = useState('');
  const [searchConversations, setSearchConversations] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const advisorName = branding?.chatbotName || 'Aria';

  const filteredSales = sales.filter(s => 
    s.name.toLowerCase().includes(searchSales.toLowerCase()) ||
    s.recommendation.toLowerCase().includes(searchSales.toLowerCase())
  );

  const filteredRecommendations = recommendations.filter(r =>
    r.item.toLowerCase().includes(searchRecommendations.toLowerCase())
  );

  const filteredConversations = conversations.filter(c =>
    c.name.toLowerCase().includes(searchConversations.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-ai-advisors">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            AI Advisors
          </h1>
          <p className="text-gray-600">Monitor your AI beauty advisor's performance and insights</p>
        </div>
        <Link to="/admin/app-builder">
          <Button variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* Performance Header */}
          <Card className="bg-gray-50 border-0">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">{advisorName} AI Performance</h2>
                <select 
                  value={timeFilter} 
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className="px-4 py-2 border rounded-lg bg-white"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                </select>
              </div>

              {/* Stats Grid - Row 1 */}
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <Card className="border-l-4 border-l-amber-400">
                  <CardContent className="pt-6">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">TIME SAVED</p>
                    <p className="text-3xl font-bold">{stats.timeSaved}</p>
                    <p className="text-xs text-gray-400 mt-1">All Time</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-400">
                  <CardContent className="pt-6">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">CONVERSATIONS</p>
                    <p className="text-3xl font-bold">{stats.conversations} sessions</p>
                    <p className="text-xs text-gray-400 mt-1">All Time</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-400">
                  <CardContent className="pt-6">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">RECOMMENDATIONS</p>
                    <p className="text-3xl font-bold">{stats.recommendations}</p>
                    <p className="text-xs text-gray-400 mt-1">All Time</p>
                  </CardContent>
                </Card>
              </div>

              {/* Stats Grid - Row 2 */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="border-l-4 border-l-amber-400">
                  <CardContent className="pt-6">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">ORDERS SOLD</p>
                    <p className="text-3xl font-bold">{stats.ordersSold}</p>
                    <p className="text-xs text-gray-400 mt-1">All Time</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-400">
                  <CardContent className="pt-6">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">TOTAL SALES</p>
                    <p className="text-3xl font-bold">${stats.totalSales.toLocaleString()}</p>
                    <p className="text-xs text-gray-400 mt-1">All Time</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-400">
                  <CardContent className="pt-6">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">AVERAGE ORDER VALUE</p>
                    <p className="text-3xl font-bold">${stats.avgOrderValue.toLocaleString()}</p>
                    <p className="text-xs text-gray-400 mt-1">All Time</p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Sales Table */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg uppercase tracking-wide">Sales</CardTitle>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Input
                      placeholder="Search..."
                      value={searchSales}
                      onChange={(e) => setSearchSales(e.target.value)}
                      className="w-48"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>Show:</span>
                    <select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))} className="px-2 py-1 border rounded">
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>rows</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-3 text-xs text-gray-500 uppercase">Name</th>
                    <th className="py-3 text-xs text-gray-500 uppercase">Recommendation</th>
                    <th className="py-3 text-xs text-gray-500 uppercase">Source</th>
                    <th className="py-3 text-xs text-gray-500 uppercase">Value</th>
                    <th className="py-3 text-xs text-gray-500 uppercase">Time & Date</th>
                    <th className="py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-500">No sales data</td>
                    </tr>
                  ) : (
                    filteredSales.map((sale) => (
                      <tr key={sale.id} className="border-b hover:bg-gray-50">
                        <td className="py-4">{sale.name}</td>
                        <td className="py-4">{sale.recommendation}</td>
                        <td className="py-4 text-gray-600">{sale.source}</td>
                        <td className="py-4">${sale.value.toFixed(2)}</td>
                        <td className="py-4 text-gray-600">{sale.date}</td>
                        <td className="py-4">
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Recommendations Table */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg uppercase tracking-wide">Recommendations</CardTitle>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Input
                      placeholder="Search..."
                      value={searchRecommendations}
                      onChange={(e) => setSearchRecommendations(e.target.value)}
                      className="w-48"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>Show:</span>
                    <select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))} className="px-2 py-1 border rounded">
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>rows</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-3 text-xs text-gray-500 uppercase">Recommended Item</th>
                    <th className="py-3 text-xs text-gray-500 uppercase">Recommendation Count</th>
                    <th className="py-3 text-xs text-gray-500 uppercase">Last Recommended</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecommendations.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-gray-500">No recommendations data</td>
                    </tr>
                  ) : (
                    filteredRecommendations.map((rec) => (
                      <tr key={rec.id} className="border-b hover:bg-gray-50">
                        <td className="py-4">{rec.item}</td>
                        <td className="py-4">{rec.count}</td>
                        <td className="py-4 text-gray-600">{rec.lastRecommended}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Conversations Table */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg uppercase tracking-wide">Conversations</CardTitle>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Input
                      placeholder="Search..."
                      value={searchConversations}
                      onChange={(e) => setSearchConversations(e.target.value)}
                      className="w-48"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>Show:</span>
                    <select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))} className="px-2 py-1 border rounded">
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>rows</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-3 text-xs text-gray-500 uppercase">Name</th>
                    <th className="py-3 text-xs text-gray-500 uppercase">Recommendations</th>
                    <th className="py-3 text-xs text-gray-500 uppercase">Time & Date</th>
                    <th className="py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConversations.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-gray-500">No conversations data</td>
                    </tr>
                  ) : (
                    filteredConversations.map((conv) => (
                      <tr key={conv.id} className="border-b hover:bg-gray-50">
                        <td className="py-4">{conv.name}</td>
                        <td className="py-4">
                          {conv.recommendations.length > 0 ? (
                            <div className="space-y-1">
                              {conv.recommendations.map((rec, i) => (
                                <div key={i} className="text-sm">{rec}</div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-4 text-gray-600">{conv.date}</td>
                        <td className="py-4">
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Settings className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">AI Advisor Settings</h3>
                <p className="text-gray-500 mb-4">Configure your AI advisor in App Builder</p>
                <Link to="/admin/app-builder">
                  <Button>
                    <Settings className="h-4 w-4 mr-2" />
                    Go to App Builder
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAIAdvisors;
